import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({
    open,
    onOpenChange,
}: HelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">
                        Form Engine Microservices
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700 leading-relaxed">

                    <h2 className="text-lg font-semibold text-black">
                        Forms
                    </h2>

                    {/* PORTAL FORMS */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Portal Forms
                        </h3>

                        <p>
                            Portal Forms are mainly used for public-facing portals
                            to collect information from users.
                        </p>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 1 –
                                </span>
                                <span>
                                    Navigate to the Portal Forms section and click
                                    <span className="font-medium"> Add Form</span>.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 2 –
                                </span>
                                <span>
                                    Enter the Form Name.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 3 –
                                </span>
                                <span>
                                    Optionally select an existing form from the
                                    dropdown to copy.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 4 –
                                </span>
                                <span>
                                    Select a Partner if the form is partner-specific.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 5 –
                                </span>
                                <span>
                                    Save the form to create and assign it.
                                </span>
                            </li>
                        </ol>
                    </section>

                    {/* WORKFLOW FORMS */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Workflow Forms
                        </h3>

                        <p>
                            Workflow Forms are mainly used to support workflow
                            execution and workflow steps.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Click Add Form.</li>
                            <li>Enter the Form Name.</li>
                            <li>Copy an existing form if required.</li>
                            <li>Select a Partner if applicable.</li>
                            <li>Save the form.</li>
                        </ul>
                    </section>

                    {/* PARTNER FORMS */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Partner Forms
                        </h3>

                        <p>
                            Partner Forms are mainly used for partner-facing portals.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Click Add Form.</li>
                            <li>Enter the Form Name.</li>
                            <li>Copy an existing form if required.</li>
                            <li>Select a Partner if applicable.</li>
                            <li>Save the form.</li>
                        </ul>
                    </section>

                    {/* ADHOC FORMS */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Adhoc Forms
                        </h3>

                        <p>
                            Adhoc Forms can be created from the Adhoc Forms section.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Click Add Form.</li>
                            <li>Enter the Form Name.</li>
                            <li>Copy an existing form if required.</li>
                            <li>Select a Partner if applicable.</li>
                            <li>Save the form.</li>
                        </ul>
                    </section>

                    {/* LIBRARY FORMS */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Library Forms
                        </h3>

                        <p>
                            Library Forms are used to generate documents such as
                            purchase orders where values automatically change
                            based on provided inputs.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Click Add Form.</li>
                            <li>Enter the Form Name.</li>
                            <li>Copy an existing form if required.</li>
                            <li>Select a Partner if applicable.</li>
                            <li>Save the form.</li>
                        </ul>
                    </section>

                    {/* FORM VERSION */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Form Version
                        </h3>

                        <p>
                            Every time a form is edited and saved, a new version
                            is created. This helps maintain version history and
                            track changes over time.
                        </p>
                    </section>

                    {/* VIEW FORM */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            View Form
                        </h3>

                        <p>
                            Designed forms can be viewed using the
                            <span className="font-medium"> View Form </span>
                            option available in the Action column.
                        </p>
                    </section>

                    {/* TEMPLATES */}
                    <h2 className="text-lg font-semibold text-black">
                        Templates
                    </h2>

                    <section className="space-y-3">
                        <p>
                            Templates are pre-designed layouts that allow users
                            to create documents quickly and consistently.
                        </p>

                        <h3 className="text-base font-bold text-black">
                            Letter Templates
                        </h3>

                        <p>
                            Pre-formatted templates used for creating formal or
                            informal letters.
                        </p>

                        <h3 className="text-base font-bold text-black">
                            Email Templates
                        </h3>

                        <p>
                            Pre-written email structures used for repeated
                            communication.
                        </p>

                        <h3 className="text-base font-bold text-black">
                            Survey Templates
                        </h3>

                        <p>
                            Ready-made question formats used to collect
                            information and feedback in a structured way.
                        </p>
                    </section>

                    {/* FORM DESIGNER */}
                    <h2 className="text-lg font-semibold text-black">
                        Form Designer
                    </h2>

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            How to Build Form
                        </h3>

                        <p>
                            Click the Build Form link to open the Form Designer
                            and start designing the selected form.
                        </p>

                        <h4 className="font-semibold text-black">
                            Step 1 - Add Columns and Layout
                        </h4>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Add columns to define the layout.</li>
                            <li>Choose how many fields appear in each column.</li>
                            <li>Add multiple columns at once or individually.</li>
                            <li>Modify grid settings from the configuration panel.</li>
                        </ul>

                        <h4 className="font-semibold text-black">
                            Step 2 - Add Components
                        </h4>

                        <p>
                            Drag and drop components from the Components panel.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Text Field</li>
                            <li>Dropdown</li>
                            <li>Checkbox</li>
                            <li>Radio Button</li>
                            <li>Table</li>
                            <li>Link</li>
                            <li>Upload File</li>
                            <li>View Image</li>
                            <li>Map</li>
                            <li>Capture Image</li>
                        </ul>

                        <h4 className="font-semibold text-black">
                            Step 3 - Configure Data Binding
                        </h4>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Select Data Type.</li>
                            <li>Select Attribute Type.</li>
                            <li>Configure field mapping.</li>
                        </ul>

                        <h4 className="font-semibold text-black">
                            Step 4 - Preview and Publish
                        </h4>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Click Preview to review the form.</li>
                            <li>Verify fields and layout.</li>
                            <li>Publish the form once confirmed.</li>
                        </ul>
                    </section>

                    {/* FORM THEME */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Form Theme
                        </h3>

                        <p>
                            Users can apply previously created themes to change
                            the appearance and styling of forms.
                        </p>
                    </section>

                    {/* MANAGE THEME */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Manage Theme
                        </h3>

                        <p>
                            Users can create and manage multiple themes to
                            customize:
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Background Color</li>
                            <li>Text Color</li>
                            <li>Table Styling</li>
                            <li>Layout and View Settings</li>
                        </ul>
                    </section>

                    {/* AUDIT LOG */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Audit Log
                        </h3>

                        <p>
                            The Audit Log records all system errors related to
                            the Forms Manager.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Validation Errors</li>
                            <li>Authentication Errors</li>
                            <li>Database Errors</li>
                        </ul>

                        <div>
                            <p className="font-medium">
                                Each log entry includes:
                            </p>

                            <ul className="list-disc ml-5 mt-1 space-y-1">
                                <li>IP Address</li>
                                <li>Status Code</li>
                                <li>Error Timestamp</li>
                                <li>Error Type</li>
                            </ul>
                        </div>
                    </section>

                    {/* AI CONSOLE */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            AI Console
                        </h3>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-2 max-h-64 overflow-y-auto">
                            <p className="font-semibold">
                                Example Prompts
                            </p>

                            <p>
                                Create a form using first name, last name,
                                contact number and barcode with name
                                REGISTERATION and formtype WorkflowForm
                            </p>

                            <p>
                                Create a subcontractor form with name REGISTTT
                                and formtype WorkflowForm
                            </p>

                            <p>
                                Create a form for Datatype with name REG and
                                formtype WorkflowForm
                            </p>
                        </div>

                        <div>
                            <p className="font-medium">
                                AI Features
                            </p>

                            <ul className="list-disc ml-5 mt-1 space-y-1">
                                <li>Manual Form Creation</li>
                                <li>AI-Assisted Form Generation</li>
                                <li>Automatic Field Mapping</li>
                            </ul>
                        </div>
                    </section>

                </div>
            </DialogContent>
        </Dialog>
    );
}
